import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Calendar, Clock, Download, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServizioDettaglio() {
  const { id } = useParams();
  const { user } = useAuth();
  const siteSettings = useSiteSettings();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  const canSeePrices = user && (user.mostra_prezzi || user.role === 'admin' || user.role === 'operator');
  const globalPrices = siteSettings.mostra_prezzi_globale === true;
  const showPrices = globalPrices && canSeePrices;
  const bookingActive = siteSettings.prenotazioni_attive !== false;

  useEffect(() => {
    const params = showPrices ? { include_prices: true } : {};
    axios.get(`${API}/services/${id}`, { params })
      .then(r => setService(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, showPrices]);

  if (loading) return (
    <div className="max-w-4xl mx-auto px-4 py-20">
      <Skeleton className="h-8 w-64 mb-4" />
      <Skeleton className="h-64 rounded-xl mb-6" />
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );

  if (!service) return (
    <div className="max-w-4xl mx-auto px-4 py-20 text-center">
      <p className="text-lg text-muted-foreground mb-4">Servizio non trovato</p>
      <Link to="/servizi"><Button variant="outline"><ArrowLeft size={16} className="mr-2" /> Torna ai servizi</Button></Link>
    </div>
  );

  return (
    <div>
      <section className="hero-wash py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/servizi" className="inline-flex items-center text-sm text-[#238A92] hover:underline mb-4">
            <ArrowLeft size={16} className="mr-1" /> Tutti i servizi
          </Link>
          <Badge className="mb-3 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">{service.categoria}</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2A37] mb-4" style={{fontFamily:'Space Grotesk'}}>{service.nome}</h1>
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
            <span className="flex items-center gap-2"><Clock size={16} /> {service.durata_minuti} minuti</span>
            {showPrices && service.prezzo && <span className="text-lg font-bold text-[#238A92]">{service.prezzo.toFixed(2)} EUR</span>}
          </div>
        </div>
      </section>

      <section className="py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Images */}
          {(service.immagine_url || service.immagine_url_2) && (
            <div className={`grid ${service.immagine_url && service.immagine_url_2 ? 'grid-cols-2' : 'grid-cols-1'} gap-4 mb-8`}>
              {service.immagine_url && (
                <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-md)]">
                  <img src={service.immagine_url} alt={service.nome} className="w-full h-[300px] object-cover" />
                </div>
              )}
              {service.immagine_url_2 && (
                <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-md)]">
                  <img src={service.immagine_url_2} alt={`${service.nome} 2`} className="w-full h-[300px] object-cover" />
                </div>
              )}
            </div>
          )}

          {/* Short description */}
          {service.descrizione && (
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">{service.descrizione}</p>
          )}

          {/* Detailed description */}
          {service.descrizione_dettaglio && (
            <div className="prose max-w-none mb-8">
              {service.descrizione_dettaglio.split('\n').map((p, i) => (
                p.trim() ? <p key={`detail-${i}`} className="text-muted-foreground leading-relaxed mb-4">{p}</p> : null
              ))}
            </div>
          )}

          {/* Downloadable document */}
          {service.documento_url && (
            <Card className="mb-8">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <FileText size={20} className="text-red-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Documento informativo</p>
                    <p className="text-xs text-muted-foreground">{service.documento_url.split('/').pop()}</p>
                  </div>
                </div>
                <a href={service.documento_url} target="_blank" rel="noopener noreferrer" download>
                  <Button variant="outline" size="sm">
                    <Download size={14} className="mr-1" /> Scarica
                  </Button>
                </a>
              </CardContent>
            </Card>
          )}

          {/* CTA */}
          <div className="mt-10 pt-6 border-t flex items-center justify-between">
            <Link to="/servizi">
              <Button variant="outline"><ArrowLeft size={16} className="mr-2" /> Altri servizi</Button>
            </Link>
            <Link to={bookingActive ? `/prenota?service=${service?.id}` : "/contatti"}>
              <Button className="bg-[#238A92] hover:bg-[#0F5E66] text-white">
                <Calendar size={14} className="mr-1" /> {bookingActive ? 'Prenota Appuntamento' : 'Contattaci'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
